import {
  proStudioUrqlGql as urqlGql,
  PypeRuleInput,
  RuleCreationTypeEnum,
} from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { checkForErrors, getPypeRuleGroups, type RuleSet } from '../../utils';
// Imported from the module rather than the `../../utils` barrel on purpose: the
// unit suite mocks that barrel wholesale, so a barrel import would make this
// guard a stub in exactly the tests that exist to prove it.
import { checkMaskingPattern } from '../../utils/masking-pattern-validation';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

/**
 * Read-only rules every new rule set starts with — Credit Card + US SSN masks.
 * Marked SYSTEM so the UI renders them locked (not editable/deletable).
 */
const DEFAULT_SYSTEM_RULES: PypeRuleInput[] = [
  {
    // The name states the covered brands so the rule is self-describing without
    // reading the regex, matching how the SSN rule already names its scope
    // (PSTUDIO-3804). Kept as ONE rule with one combined pattern — splitting it
    // per brand was considered and rejected: the regex already ORs the brands
    // together, and there is no case for redacting Visa but not Amex.
    name: 'Credit Card (Visa, Mastercard, Amex, Discover, Diners Club, JCB)',
    description:
      'Masks Visa, Mastercard, American Express, Discover, Diners Club and JCB card numbers.',
    type: RuleCreationTypeEnum.System,
    // Byte-identical to the pattern the backend seeds into a project's default
    // rule set (the UI shows it as "Standard Rules"), so a rule set created here
    // masks exactly what one created with the project does — PSTUDIO-4050.
    //
    // The separator class is `[ -]*?`, not `[ -]?`. That is parity, not a
    // strict upgrade: `*?` accepts repeated separators, so `4111  1111  1111
    // 1111` and `5555 - 5555 - 5555 - 4444` are masked where `?` missed them —
    // but the lazy form also stops after 13 digits when a separator follows
    // (`4111 1111 1111 1 111`), which `?` covered. Both are the backend's
    // behaviour; widening only this copy would break the parity the ticket is
    // about, so the limitation belongs in a backend ticket.
    //
    // Rule sets created BEFORE this change keep the old string server-side, and
    // SYSTEM rules are not user-editable, so nobody can fix one by hand. The
    // read-time override used for the display name (`SYSTEM_RULE_DISPLAY_NAMES`
    // in `get-pype-rule-groups.ts`, PSTUDIO-3804) is deliberately NOT applied
    // here: a name is cosmetic, a pattern is the control the backend actually
    // masks with, so overriding it on read would make this screen advertise a
    // protection the widget does not apply. Reconciling them needs a backend
    // migration.
    rules: [
      '\\b(?:4[ -]*?(?:\\d[ -]*?){12}(?:(?:\\d[ -]*?){3})?|(?:5[1-5][ -]*?(?:\\d[ -]*?){2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[ -]*?(?:\\d[ -]*?){12}|3[47][ -]*?(?:\\d[ -]*?){13}|3(?:0[0-5]|[68][ -]*?(?:\\d[ -]*?))[ -]*?(?:\\d[ -]*?){11}|6(?:011|5[ -]*?(?:\\d[ -]*?){2})[ -]*?(?:\\d[ -]*?){12}|(?:2131|1800|35[ -]*?(?:\\d[ -]*?){3})[ -]*?(?:\\d[ -]*?){11})\\b',
    ],
  },
  {
    name: 'Social Security Number (US)',
    description: 'social security mask',
    type: RuleCreationTypeEnum.System,
    rules: [
      '(?!219-09-9999|078-05-1120)(?!666|000|9\\d{2})\\d{3}-(?!00)\\d{2}-(?!0{4})\\d{4}',
    ],
  },
];

/** Creates a new masking rule set, then re-fetches the project's rule sets. */
export const createPypeRuleGroup = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{ pypeRuleGroups: RuleSet[]; cb?: () => void } | void> => {
  if (event.type !== 'createPypeRuleGroup') {
    return;
  }

  const projectId = ctx.project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  const { name, description, rulesToAdd, cb } = event.payload;

  // PSTUDIO-4301. `RuleEditor`'s Save gate closes the authoring surface, and this
  // is the other way a pattern can arrive: the payload. It is unused today —
  // `CreateRuleSetCard` passes a name only — so the point of the check is the
  // caller that does not exist yet, because the failure it would reopen is
  // silent (the widget compiles each rule in a try/catch, caches `null` and
  // masks nothing).
  //
  // REFUSED, not filtered. Dropping the bad patterns and creating the rest would
  // be the same silent failure in a new place: the caller would be told the rule
  // set was created and never learn which of its rules went missing.
  //
  // The SYSTEM seeds below are deliberately NOT judged — they are the product's
  // own patterns, they come from the backend, and a guard that could refuse them
  // would break every new rule set.
  const unusablePatterns = (rulesToAdd ?? []).flatMap((rule) =>
    (rule?.rules ?? []).filter(
      (pattern) =>
        typeof pattern !== 'string' || !checkMaskingPattern(pattern).valid
    )
  );

  if (unusablePatterns.length > 0) {
    throwErrorWithId(
      `Cannot create a masking rule set: ${unusablePatterns.length} of the supplied patterns are empty, uncompilable, or too slow to run`
    );
    return;
  }

  const response = await urqlGql.createPypeRuleGroup({
    projectId,
    name,
    description,
    // Seed every new rule set with the default SYSTEM masks, plus any
    // caller-provided rules.
    rulesToAdd: [...DEFAULT_SYSTEM_RULES, ...(rulesToAdd ?? [])],
  });
  checkForErrors(response);

  const pypeRuleGroups = await getPypeRuleGroups({ projectId });

  return { pypeRuleGroups, cb };
};
